import type { AttestationCyclePartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Attestation_Record_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import dayjs from 'dayjs';
import type {
  AttestationCycleStatus,
  CalculatedAttestationStatus,
} from 'src/pages/attestations/types';

export const calculateCycleStatus = (
  cycle: AttestationCyclePartsFragment
): AttestationCycleStatus => {
  const hasElapsedRecords = (
    records: AttestationCyclePartsFragment['records']
  ): boolean => {
    const dateHasElapsed = (date: string | null | undefined): boolean =>
      date ? new Date(date) < new Date() : false;

    if (
      records.some(
        (record) =>
          dateHasElapsed(record.ExpiresAt) &&
          record.AttestationStatus !== Attestation_Record_Status_Enum.Attested
      )
    ) {
      return true;
    }

    return false;
  };

  if (hasElapsedRecords(cycle.records) && cycle.Status === 'active') {
    return 'overdue';
  }

  return cycle.Status as 'active' | 'concluded';
};

export const isOverdue = (record: {
  Active: boolean;
  ExpiresAt?: string | null;
}): boolean => {
  if (record.Active && record.ExpiresAt && dayjs(record.ExpiresAt).isBefore()) {
    return true;
  }

  return false;
};

export const calculateAttestationStatus = (
  record: {
    Active: boolean;
    ExpiresAt?: string | null;
    AttestationStatus: Attestation_Record_Status_Enum;
  },
  options: { useAttestationImprovements: boolean }
): CalculatedAttestationStatus => {
  //todo: remove the useAttestationImprovements flag and related code when the feature is fully rolled out
  if (options.useAttestationImprovements) {
    if (isOverdue(record)) {
      return 'overdue';
    }

    return record.AttestationStatus;
  }

  return getCorrectedAttestationStatus(record);
};

/**
 * Return the attestation status with the expiry date taken into account
 * @param record
 * @returns
 */
const getCorrectedAttestationStatus = (record: {
  AttestationStatus: Attestation_Record_Status_Enum;
  ExpiresAt?: null | string | undefined;
}): Attestation_Record_Status_Enum =>
  dayjs(record.ExpiresAt).isBefore() &&
  record.AttestationStatus !== Attestation_Record_Status_Enum.Attested &&
  record.AttestationStatus !== Attestation_Record_Status_Enum.NotRequired
    ? Attestation_Record_Status_Enum.Expired
    : record.AttestationStatus;
